import { adjustArrays } from '@/utils/array'

import type {
  HouseSlug,
  UnknownDestinationViewModelWeb,
} from 'lc-services/types'

export type LastSearchItem = {
  id: `${number}` | 'area' | 'area_unknown'
  name: string
  startDate?: Date | null
  endDate?: Date | null
  guest?: number
}
export type HouseItem = {
  id: `${number}`
  name: string
  slug?: HouseSlug
  first_photo_url: string | null
}
export type DestinationItem = {
  id: `${number}`
  name: string
  first_photo_url?: string | null
}

export type SearchInputState = {
  allDestinations: {
    ancestry?: string
    child_ids: number[]
    clusterized_name: HouseSlug
    first_photo_url: string
    houses_count: number
    id: number
    is_country: boolean
    name: HouseSlug
    parent_id: number | null
  }[]
  breadcrumbsDestinationId?: number
  data: {
    location: {
      coords?: number[]
      id?: `${number}` | 'area' | 'area_unknown'
      name?: string
      slug?: HouseSlug
      type?: 'destination' | 'house'
    }
    startDate: Date | null
    endDate: Date | null
    guest: number
  }
  meta: {
    searchQuery: string
    searchQueryLast: string
  }
  submittedSearch: boolean
  destinations: DestinationItem[]
  houses: HouseItem[]
  topDestinations: DestinationItem[]
  lastSearches: LastSearchItem[]
  unknownDestinations: UnknownDestinationViewModelWeb[]
}

export const useSearchInput = () => {
  const { $httpCookies, $lcServicesSearch } = useNuxtApp()
  const route = useRoute()
  const localePath = useLocalePath()
  const { locale, t } = useI18n()
  const { algoliaIds, userIsAdmin, userIsPartner, mode } = useAuth()
  const { searchController, searchPresenter } = $lcServicesSearch
  const { pageType, trackEvent } = useTracking()
  const { ancestries, isLp } = useDestination()

  const PLACEHOLDER_DESTI =
    'production/assets/images/search/placeholder-desti.jpg'
  const lastQueryWithoutResults = ref('')

  const searchInputState = useState<SearchInputState>('search-input', () => ({
    allDestinations: [],
    breadcrumbsDestinationId: undefined,
    data: {
      location: {},
      startDate: null,
      endDate: null,
      guest: 0,
    },
    destinations: [],
    houses: [],
    meta: {
      searchQuery: '',
      searchQueryLast: '',
    },
    submittedSearch: false,
    topDestinations: [],
    lastSearches: [],
    unknownDestinations: [],
  }))

  const lastSearches = computed(() => searchInputState.value.lastSearches)
  const data = computed(() => searchInputState.value.data)
  const allDestinations = computed(() => searchInputState.value.allDestinations)
  const allCountries = computed(() =>
    allDestinations.value.filter((d) => d.is_country),
  )
  const breadcrumbsDestinationId = computed(
    () => searchInputState.value.breadcrumbsDestinationId,
  )
  const destinationId = computed(() =>
    data.value.location.id && !isNaN(Number(data.value.location.id))
      ? (data.value.location.id as `${number}`)
      : null,
  )
  const isSearchingForUnknownDestination = computed(
    () =>
      data.value.location.id === 'area_unknown' &&
      'lat' in route.query &&
      'lng' in route.query,
  )
  const destinations = computed(() => searchInputState.value.destinations)
  const houses = computed(() => searchInputState.value.houses)
  const unknownDestinations = computed(
    () => searchInputState.value.unknownDestinations,
  )
  const meta = computed(() => searchInputState.value.meta)
  const submittedSearch = computed(() => searchInputState.value.submittedSearch)
  const topDestinations = computed(() => searchInputState.value.topDestinations)

  const routeName = computed(() => getRouteName(route))
  // This computed determines the behavior of the Search button in the Header
  // -> true = triggers a reload of the search results of the current page
  // -> false = triggers a navigation to another page (search or product page)
  const pageReloadSearch = computed(() => {
    // on a landing-page => navigation
    if (isLp.value) return false
    // user cleared the destination, or selected a house => navigation
    if (data.value.location.type !== 'destination') return false
    // on the search page or filtered SEO page, we want to stay on the same page => reload
    if (['search', 'destination-slug-filter'].includes(routeName.value))
      return true
    // on the destination page (not LP)
    if (destinationId.value && routeName.value === 'destination-name') {
      // the destination was not changed nor cleared => reload
      return destinationId.value === ancestries.value?.destinationId
    }
    // all other cases => navigation
    return false
  })

  const optionsSearchEmpty = computed(() => [
    {
      title: t('search.topDestinations'),
      key: 'destination' as const,
      icon: 'pinAlt' as const,
      items: topDestinations.value,
    },
  ])

  const optionsSearch = computed(() => {
    const [newDestinations, newHouses] = adjustArrays<
      HouseItem | DestinationItem
    >({ minPerArray: 2, maxItems: 6 }, destinations.value, houses.value)

    return [
      ...(newDestinations.length
        ? [
            {
              title: t('search.destinations'),
              key: 'destination',
              icon: 'pinAlt',
              items: newDestinations,
            },
          ]
        : []),
      ...(newHouses.length
        ? [
            {
              title: t('search.homes'),
              key: 'house',
              icon: 'home',
              items: newHouses,
            },
          ]
        : []),
    ] as Array<
      | {
          title: string
          key: 'destination'
          icon: 'pinAlt'
          items: DestinationItem[]
        }
      | {
          title: string
          key: 'house'
          icon: 'home'
          items: HouseItem[]
        }
    >
  })
  const getCountryByName = (input: string) => (desti: { name: HouseSlug }) =>
    desti.name[locale.value].toLowerCase() === input

  const unknownDestination = computed(() => {
    // when navigating to the page or refreshing it
    if (route.query.fullName) {
      const [queryName, ...items] = route.query.fullName.toString().split(',')
      // The country name should be the last element of the fullName
      const [country] = items.length ? [...items].reverse() : [queryName]
      if (!country) return null
      return {
        country: allCountries.value.find(
          getCountryByName(country.trim().toLowerCase()),
        ),
        queryName,
      }
    }
    // when selecting an unknown destination from the search page
    if (!unknownDestinations.value?.length || !data.value.location.name)
      return null
    const selection = unknownDestinations.value.find(
      (u) => u.fullName === data.value.location.name,
    )
    const { country, matchedQuery } = selection ?? {}
    if (!country || !matchedQuery) return null

    return {
      country: allCountries.value.find(
        getCountryByName(country.name.toLowerCase()),
      ),
      queryName: matchedQuery.map(({ char }) => char).join(''),
    }
  })

  const fetchTopAndAllDestinations = async () => {
    if (
      searchInputState.value.allDestinations.length &&
      searchInputState.value.topDestinations.length
    )
      return

    const data = await $fetch(`/api/${locale.value}/search-input`)

    searchInputState.value.allDestinations = data.all
    searchInputState.value.topDestinations = data.popular
  }

  const fetchAllDestinations = async () => {
    if (searchInputState.value.allDestinations.length)
      return searchInputState.value.allDestinations

    const data = await $fetch(`/api/${locale.value}/search-input`)

    searchInputState.value.allDestinations = data.all
    return data.all
  }

  const fetchDestinationsAndHouses = async (value: string) => {
    if (!value) resetDestinationsAndHouses()
    else if (lastQueryWithoutResults.value)
      try {
        if (new RegExp(lastQueryWithoutResults.value).test(value))
          return undefined
      } catch {
        // In case the RegExp is invalid
        return undefined
      }

    await searchController.searchHousesAndDestinations(
      value,
      locale.value,
      mode.value,
      algoliaIds.value,
    )

    searchInputState.value.destinations =
      searchPresenter.vm.destinationsResult.map((d) => ({
        first_photo_url: d.first_photo_url || null,
        id: `${+d.id}`,
        name: d.name,
      }))

    searchInputState.value.houses = searchPresenter.vm.housesResult.map(
      (h) => ({
        first_photo_url: h.first_photo_url || null,
        id: `${+h.id}`,
        name: h.name,
        slug: h.slug,
      }),
    )

    if (searchInputState.value.destinations.length) {
      searchInputState.value.unknownDestinations = []
    } else if (!userIsAdmin.value && !userIsPartner.value) {
      const matches = await searchController.searchUnknownDestination(value, {
        locale: locale.value,
      })
      if (Array.isArray(matches)) {
        const { suggestions } = searchPresenter.displayUnknownDestinations(
          matches,
          value,
        )
        searchInputState.value.unknownDestinations = suggestions
      }
    }

    lastQueryWithoutResults.value =
      !searchInputState.value.houses.length &&
      !searchInputState.value.destinations.length &&
      !searchInputState.value.unknownDestinations.length
        ? value
        : ''

    if (lastQueryWithoutResults.value)
      trackEvent({
        event: 'search_without_suggestions',
        search_query: value,
      })
  }

  const goToHouse = () => {
    trackingGoTo('house')

    navigateTo(
      localePath({
        name: 'house-slug',
        params: { slug: data.value.location?.slug?.[locale.value] },
      }),
    )
  }

  const goToSearch = () => {
    trackingGoTo('destination')

    $httpCookies.remove('search_params')

    navigateTo(
      localePath({
        name: 'search',
        query: {
          ...(data.value.location.coords
            ? {
                fullName: data.value.location.name,
                lat: data.value.location.coords[0],
                lng: data.value.location.coords[1],
              }
            : destinationId.value
              ? userIsAdmin.value || userIsPartner.value
                ? { multipleDestinationsIds: destinationId.value }
                : { destinationId: destinationId.value }
              : {}),
          ...(data.value.guest
            ? { numericFilters: `capacity >= ${data.value.guest}` }
            : {}),
          ...(data.value.startDate && data.value.endDate
            ? {
                startDate: $dayjs(data.value.startDate).format('YYYY-MM-DD'),
                endDate: $dayjs(data.value.endDate).format('YYYY-MM-DD'),
              }
            : {}),
        },
      }),
    )
  }

  const trackingGoTo = (type: 'destination' | 'house') => {
    const searchQuery = meta.value.searchQueryLast
    const suggestion = searchQuery.length
      ? type === 'destination'
        ? 'location'
        : 'house'
      : 'default'

    const filterAppliedName = [
      destinationId.value && 'desti_id',
      data.value.startDate && data.value.endDate && 'dates_selected',
      data.value.guest && 'capacity',
    ].filter(Boolean)

    trackEvent({
      event: 'search_started',
      filter_applied_name: filterAppliedName,
      source: pageType.value,
      page_type: pageType.value,
      suggestion,
      ...(searchQuery.length
        ? { search_query: searchQuery, is_autocomplete: Boolean(searchQuery) }
        : {}),
    })
  }

  const trackingInputClick = (field: 'destination' | 'calendar' | 'guests') => {
    trackEvent({
      event: 'search_input_clicked',
      field,
      page_type: pageType.value,
      source: pageType.value,
    })
  }

  const keepLastSearchQuery = () => {
    setMeta('searchQueryLast', meta.value.searchQuery)
    setMeta('searchQuery', '')
  }

  const resetDestinationsAndHouses = () => {
    searchInputState.value.destinations = []
    searchInputState.value.houses = []
    searchInputState.value.unknownDestinations = []
  }

  const setData = <K extends keyof SearchInputState['data']>(
    key: K,
    value: SearchInputState['data'][K],
  ) => {
    searchInputState.value.data[key] = value
  }

  const setMeta = <K extends keyof SearchInputState['meta']>(
    key: K,
    value: SearchInputState['meta'][K],
  ) => {
    searchInputState.value.meta[key] = value
  }

  const setBreadcrumbsDestinationId = (id?: number) => {
    searchInputState.value.breadcrumbsDestinationId = id
  }

  const prefillBreadcrumbsDestination = () => {
    if (data.value.location?.id) {
      const desti = allDestinations.value.find(
        (d) => d.id === Number(data.value.location.id),
      )
      if (desti?.child_ids.length) {
        setBreadcrumbsDestinationId(desti.id)
      } else {
        setBreadcrumbsDestinationId(desti?.parent_id || undefined)
      }
    }
  }

  const resetData = () => {
    searchInputState.value.data = {
      location: {},
      startDate: null,
      endDate: null,
      guest: 0,
    }
    searchInputState.value.meta = {
      searchQuery: '',
      searchQueryLast: '',
    }
    searchInputState.value.unknownDestinations = []
  }

  const setSubmittedSearch = (value: boolean) => {
    searchInputState.value.submittedSearch = value
  }

  const setLastSearches = (value: LastSearchItem[]) => {
    searchInputState.value.lastSearches = value
  }

  const storeCurrentSearch = () => {
    const { location, guest, startDate, endDate } = data.value
    if (
      import.meta.server ||
      !location.id ||
      !location.name ||
      location.type === 'house'
    )
      return

    const currSearch: LastSearchItem = {
      id: location.id,
      name: location.name,
      ...(guest && { guest }),
      ...(startDate && endDate && { startDate, endDate }),
    }
    const filteredSearches = lastSearches.value.filter(
      (item) => !isReplacedByCurrentSearch(item, currSearch),
    )

    const threeLastSearches = [currSearch, ...filteredSearches.slice(0, 2)]
    setLastSearches(threeLastSearches)
    persistLastSearches(threeLastSearches)
  }

  const isReplacedByCurrentSearch = (
    item: LastSearchItem,
    currSearch: LastSearchItem,
  ): boolean => {
    const sameDestination = Number(item.id) === Number(currSearch.id)
    if (!sameDestination) return false

    const itemHasDates = item.startDate && item.endDate
    const currHasDates = currSearch.startDate && currSearch.endDate
    const datesChanged =
      itemHasDates &&
      currHasDates &&
      (item.startDate!.getTime() !== currSearch.startDate!.getTime() ||
        item.endDate!.getTime() !== currSearch.endDate!.getTime())

    if (datesChanged) return false
    if (itemHasDates && !currHasDates) return false
    if (!itemHasDates && currHasDates) return true
    if (item.guest !== currSearch.guest) return true
    if (sameDestination) return true

    return false
  }

  const persistLastSearches = (lastSearches: LastSearchItem[]) => {
    try {
      localStorage.setItem('last-search', JSON.stringify(lastSearches))
    } catch (err) {
      console.error(err)
    }
  }

  const getLastSearchesFromStorage = (): LastSearchItem[] => {
    let storedLastSearches: {
      id: number | string
      name: string
      startDate?: string | null
      endDate?: string | null
      guest?: number
    }[] = []

    try {
      storedLastSearches =
        JSON.parse(localStorage.getItem('last-search') as string) || []
    } catch (err) {
      console.error(err)
    }

    return storedLastSearches.map((ls) => ({
      id: `${ls.id as number}`,
      name: ls.name,
      ...(ls.startDate &&
        ls.endDate && {
          startDate: new Date(ls.startDate),
          endDate: new Date(ls.endDate),
        }),
      ...(ls.guest && { guest: ls.guest }),
    }))
  }

  const initLastSearches = () => setLastSearches(getLastSearchesFromStorage())

  return {
    allDestinations,
    breadcrumbsDestinationId,
    data,
    destinationId,
    destinations,
    fetchAllDestinations,
    fetchDestinationsAndHouses,
    fetchTopAndAllDestinations,
    goToHouse,
    goToSearch,
    houses,
    initLastSearches,
    isSearchingForUnknownDestination,
    keepLastSearchQuery,
    lastSearches,
    meta,
    optionsSearch,
    optionsSearchEmpty,
    pageReloadSearch,
    PLACEHOLDER_DESTI,
    prefillBreadcrumbsDestination,
    resetData,
    resetDestinationsAndHouses,
    setBreadcrumbsDestinationId,
    setData,
    setLastSearches,
    setMeta,
    setSubmittedSearch,
    storeCurrentSearch,
    submittedSearch,
    topDestinations,
    trackingGoTo,
    trackingInputClick,
    unknownDestination,
    unknownDestinations,
  }
}
